/**
 * CEO Command Center - Utility Functions
 * 
 * Collection of pure utility functions for formatting, date manipulation,
 * and common operations. All functions are pure (no side effects) and
 * thoroughly documented.
 * 
 * @module utils/format
 * @version 10.0.0
 */

// CURRENCY FORMATTING

/**
 * Format a number as currency (EUR)
 * Uses compact notation for large values
 * 
 * @param value - Number to format
 * @param options - Formatting options
 * @returns Formatted currency string
 * 
 * @example
 * formatCurrency(1500000) // "€1.5M"
 * formatCurrency(250000)  // "€250k"
 * formatCurrency(999)     // "€999"
 * formatCurrency(null)    // "€0"
 */
export function formatCurrency(
  value: number | null | undefined,
  options: { 
    currency?: string;
    showDecimals?: boolean;
  } = {}
): string {
  const { currency = '€', showDecimals = true } = options;
  
  if (value === null || value === undefined || isNaN(value)) {
    return `${currency}0`;
  }
  
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (absValue >= 1_000_000) {
    const formatted = showDecimals 
      ? (absValue / 1_000_000).toFixed(1)
      : Math.round(absValue / 1_000_000).toString();
    return `${sign}${currency}${formatted}M`;
  }
  
  if (absValue >= 1_000) {
    return `${sign}${currency}${Math.round(absValue / 1_000)}k`;
  }
  
  return `${sign}${currency}${Math.round(absValue)}`;
}

/**
 * Format a number as percentage
 * 
 * @param value - Decimal value (0.0 - 1.0) or percentage (0 - 100)
 * @param options - Formatting options
 * @returns Formatted percentage string
 * 
 * @example
 * formatPercentage(0.75)     // "75%"
 * formatPercentage(75, { isDecimal: false }) // "75%"
 * formatPercentage(0.756, { decimals: 1 })   // "75.6%"
 */
export function formatPercentage(
  value: number | null | undefined,
  options: {
    isDecimal?: boolean;
    decimals?: number;
  } = {}
): string {
  const { isDecimal = true, decimals = 0 } = options;
  
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Parse a currency string to number
 * 
 * @param value - Currency string (e.g., "€1.5M", "$250k", "1,000")
 * @returns Parsed number or 0 if invalid
 * 
 * @example
 * parseCurrency("€1.5M")  // 1500000
 * parseCurrency("$250k")  // 250000
 * parseCurrency("1,000")  // 1000
 */
export function parseCurrency(value: string | number): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  // Remove currency symbols and whitespace
  let cleaned = value.replace(/[€$£¥,\s]/g, '');
  
  // Handle M/K suffixes
  const multiplier = cleaned.toLowerCase().endsWith('m') ? 1_000_000
    : cleaned.toLowerCase().endsWith('k') ? 1_000
    : 1;
  
  cleaned = cleaned.replace(/[mMkK]/g, '');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed * multiplier;
}

// DATE FORMATTING

/**
 * Format a date as short date (e.g., "15 Jan")
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string or "—" if invalid
 * 
 * @example
 * formatDate("2026-01-15") // "15 Jan"
 * formatDate(new Date())   // "8 Jan"
 */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return '—';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short' 
    });
  } catch {
    return '—';
  }
}

/**
 * Format a date with year (e.g., "15 Jan 2026")
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string with year
 */
export function formatDateWithYear(dateString: string | Date | null | undefined): string {
  if (!dateString) return '—';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
}

/**
 * Format a date as relative time (e.g., "in 3 days", "2 days ago")
 * 
 * @param dateString - ISO date string or Date object
 * @returns Relative time string
 * 
 * @example
 * formatRelative("2026-01-10") // "in 2d" (if today is Jan 8)
 * formatRelative("2026-01-05") // "3d ago" (if today is Jan 8)
 * formatRelative("2026-01-08") // "today"
 */
export function formatRelative(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'TBD';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 'TBD';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    
    const diffMs = target.getTime() - today.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    if (days < 0) return `${Math.abs(days)}d ago`;
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days < 7) return `in ${days}d`;
    if (days < 30) return `in ${Math.ceil(days / 7)}w`;
    return `in ${Math.ceil(days / 30)}mo`;
  } catch {
    return 'TBD';
  }
}

/**
 * Get the year from a date string
 * 
 * @param dateString - ISO date string
 * @returns Year number or null if invalid
 */
export function getYear(dateString: string | null | undefined): number | null {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date.getFullYear();
  } catch {
    return null;
  }
}

/**
 * Calculate days until a target date
 * 
 * @param dateString - Target date (ISO string)
 * @returns Number of days (negative if past)
 * 
 * @example
 * daysUntil("2026-01-15") // 7 (if today is Jan 8)
 * daysUntil("2026-01-05") // -3 (if today is Jan 8)
 */
export function daysUntil(dateString: string | Date | null | undefined): number {
  if (!dateString) return 999; // Far future if no date
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 999;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    
    return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return 999;
  }
}

/**
 * Calculate days since a past date
 * 
 * @param dateString - Past date (ISO string)
 * @returns Number of days since (0 if same day or future)
 */
export function daysSince(dateString: string | Date | null | undefined): number {
  if (!dateString) return 0;
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 0;
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday")
 * 
 * @param dateString - ISO date string or Date object
 * @returns Human-readable relative time string
 */
export function timeAgo(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'unknown';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return 'unknown';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay === 1) return 'yesterday';
    if (diffDay < 7) return `${diffDay}d ago`;
    if (diffWeek < 4) return `${diffWeek}w ago`;
    if (diffMonth < 12) return `${diffMonth}mo ago`;
    return `${Math.floor(diffDay / 365)}y ago`;
  } catch {
    return 'unknown';
  }
}

/**
 * Get today's date as ISO string (YYYY-MM-DD)
 * 
 * @returns Today's date in ISO format
 */
export function today(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Add days to today and return as ISO string
 * 
 * @param n - Number of days to add (can be negative)
 * @returns Future/past date in ISO format
 */
export function addDays(n: number): string {
  const date = new Date();
  date.setDate(date.getDate() + n);
  return date.toISOString().split('T')[0];
}

/**
 * Get week number of the year
 * 
 * @param date - Date to check (defaults to today)
 * @returns Week number (1-52)
 */
export function getWeekNumber(date: Date = new Date()): number {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diffMs = date.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.ceil((diffDays + startOfYear.getDay() + 1) / 7);
}

/**
 * Get quarter string (e.g., "Q1")
 * 
 * @param date - Date to check (defaults to today)
 * @returns Quarter string
 */
export function getQuarter(date: Date = new Date()): string {
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `Q${quarter}`;
}

/**
 * Get time-appropriate greeting
 * 
 * @returns "Good morning", "Good afternoon", or "Good evening"
 */
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// NUMBER UTILITIES

/**
 * Clamp a number between min and max values
 * 
 * @param value - Number to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate percentage of a value relative to total
 * 
 * @param value - Current value
 * @param total - Total value
 * @returns Percentage (0-100)
 */
export function percentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Format a large number with K/M suffixes
 * 
 * @param value - Number to format
 * @returns Formatted string
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(0)}K`;
  }
  return value.toLocaleString();
}

// STRING UTILITIES

/**
 * Truncate a string to a maximum length with ellipsis
 * 
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @returns Truncated string
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

/**
 * Capitalize the first letter of a string
 * 
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Generate a unique ID
 * 
 * @param prefix - Optional prefix
 * @returns Unique ID string
 */
export function generateId(prefix: string = ''): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
}

// ARRAY UTILITIES

/**
 * Group an array of objects by a key
 * 
 * @param array - Array to group
 * @param keyFn - Function to extract group key
 * @returns Grouped object
 */
export function groupBy<T>(
  array: T[], 
  keyFn: (item: T) => string
): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const key = keyFn(item);
    groups[key] = groups[key] || [];
    groups[key].push(item);
    return groups;
  }, {} as Record<string, T[]>);
}

/**
 * Sort an array of objects by a key
 * 
 * @param array - Array to sort
 * @param keyFn - Function to extract sort key
 * @param direction - Sort direction
 * @returns Sorted array (new array, doesn't mutate)
 */
export function sortBy<T>(
  array: T[],
  keyFn: (item: T) => string | number | Date,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Remove duplicates from an array
 * 
 * @param array - Array with potential duplicates
 * @param keyFn - Optional function to determine uniqueness
 * @returns Array with duplicates removed
 */
export function unique<T>(array: T[], keyFn?: (item: T) => unknown): T[] {
  if (!keyFn) {
    return [...new Set(array)];
  }
  
  const seen = new Set();
  return array.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// VALIDATION UTILITIES

/**
 * Check if a value is empty (null, undefined, empty string, empty array)
 * 
 * @param value - Value to check
 * @returns True if empty
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Check if a string is a valid email
 * 
 * @param email - Email string to validate
 * @returns True if valid email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Check if a string is a valid ISO date
 * 
 * @param dateString - Date string to validate
 * @returns True if valid date
 */
export function isValidDate(dateString: string): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

// TAILWIND CLASS MERGE UTILITY

/**
 * Merge class names, handling Tailwind conflicts
 * Simple version - for full functionality, use tailwind-merge package
 * 
 * @param classes - Class names to merge
 * @returns Merged class string
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// EXPORT ALL

export default {
  formatCurrency,
  formatPercentage,
  parseCurrency,
  formatDate,
  formatDateWithYear,
  formatRelative,
  getYear,
  daysUntil,
  daysSince,
  today,
  addDays,
  getWeekNumber,
  getQuarter,
  getGreeting,
  clamp,
  percentage,
  formatNumber,
  truncate,
  capitalize,
  generateId,
  groupBy,
  sortBy,
  unique,
  isEmpty,
  isValidEmail,
  isValidDate,
  cn,
};
